// Offline Banner Component - Shows offline status and sync info
import { useOffline, SYNC_STATUS, EVIDENCE_STATUS } from '../context/OfflineContext'
import { useLanguage } from '../context/LanguageContext'
import { WifiOff, RefreshCw, Cloud, CloudOff, Check, AlertTriangle, Clock, Upload } from 'lucide-react'
import { Button } from './ui/button'
import { Badge } from './ui/badge'

// Main Offline Banner - Shows at top when offline
export function OfflineBanner() {
  const { isOnline, isSyncing, syncStats, forceSync } = useOffline()
  const { locale } = useLanguage()
  const lang = locale?.startsWith('es') ? 'es' : 'en'

  if (isOnline && syncStats.pendingTasks === 0 && syncStats.pendingEvidences === 0) {
    return null
  }

  return (
    <div 
      className={`sticky top-0 z-50 px-4 py-2 flex items-center justify-between gap-3 transition-colors ${
        isOnline 
          ? 'bg-amber-500/20 border-b border-amber-500/30' 
          : 'bg-rose-500/20 border-b border-rose-500/30'
      }`}
      data-testid="offline-banner"
    >
      <div className="flex items-center gap-2 min-w-0">
        {isOnline ? (
          <Cloud className="w-4 h-4 text-amber-500 flex-shrink-0" />
        ) : (
          <WifiOff className="w-4 h-4 text-rose-500 flex-shrink-0" />
        )}
        
        <span className="text-sm font-medium truncate">
          {isOnline 
            ? (lang === 'es' ? 'Cambios pendientes' : 'Pending changes')
            : (lang === 'es' ? 'Modo Offline' : 'Offline Mode')
          }
        </span>
        
        {(syncStats.pendingTasks > 0 || syncStats.pendingEvidences > 0) && (
          <Badge variant="secondary" className="text-xs px-2 py-0 flex-shrink-0">
            {syncStats.pendingTasks > 0 && (
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {syncStats.pendingTasks}
              </span>
            )}
            {syncStats.pendingTasks > 0 && syncStats.pendingEvidences > 0 && ' · '}
            {syncStats.pendingEvidences > 0 && (
              <span className="flex items-center gap-1">
                <Upload className="w-3 h-3" />
                {syncStats.pendingEvidences}
              </span>
            )}
          </Badge>
        )}
      </div>
      
      {isOnline && (
        <Button
          variant="ghost"
          size="sm"
          onClick={forceSync}
          disabled={isSyncing}
          className="h-7 px-2 text-xs flex-shrink-0"
          data-testid="sync-now-btn"
        >
          <RefreshCw className={`w-3 h-3 mr-1 ${isSyncing ? 'animate-spin' : ''}`} />
          {isSyncing 
            ? (lang === 'es' ? 'Sincronizando...' : 'Syncing...')
            : (lang === 'es' ? 'Sincronizar' : 'Sync now')
          }
        </Button>
      )}
    </div>
  )
}

// Sync Status Badge - Shows on individual task cards
export function TaskSyncBadge({ syncStatus, className = '' }) {
  const { locale } = useLanguage()
  const lang = locale?.startsWith('es') ? 'es' : 'en'

  if (!syncStatus || syncStatus === SYNC_STATUS.SYNCED) {
    return null
  }

  const config = {
    [SYNC_STATUS.PENDING]: {
      icon: Clock,
      color: 'text-amber-500 border-amber-500/50',
      label: lang === 'es' ? 'Pendiente' : 'Pending'
    },
    [SYNC_STATUS.SYNCING]: {
      icon: RefreshCw,
      color: 'text-blue-500 border-blue-500/50',
      label: lang === 'es' ? 'Sincronizando' : 'Syncing',
      animate: true
    },
    [SYNC_STATUS.FAILED]: {
      icon: AlertTriangle,
      color: 'text-rose-500 border-rose-500/50',
      label: lang === 'es' ? 'Error' : 'Failed'
    }
  }

  const statusConfig = config[syncStatus] || config[SYNC_STATUS.PENDING]
  const Icon = statusConfig.icon

  return (
    <Badge 
      variant="outline" 
      className={`text-xs px-1.5 py-0 flex items-center gap-1 ${statusConfig.color} ${className}`}
      data-testid="task-sync-badge"
    >
      <Icon className={`w-3 h-3 ${statusConfig.animate ? 'animate-spin' : ''}`} />
      <span className="sr-only md:not-sr-only">{statusConfig.label}</span>
    </Badge>
  )
}

// Evidence Sync Status - Shows on evidence thumbnails
export function EvidenceSyncStatus({ status, onRetry, className = '' }) {
  const { locale } = useLanguage()
  const lang = locale?.startsWith('es') ? 'es' : 'en'

  if (!status || status === EVIDENCE_STATUS.SYNCED) {
    return null
  }

  const isFailed = status === EVIDENCE_STATUS.FAILED
  const isLocal = status === EVIDENCE_STATUS.LOCAL_ONLY
  const isSyncing = status === EVIDENCE_STATUS.SYNCING

  return (
    <div 
      className={`absolute bottom-0 left-0 right-0 py-1 px-2 flex items-center justify-center gap-1 ${
        isFailed 
          ? 'bg-rose-500/90' 
          : isLocal 
            ? 'bg-amber-500/90' 
            : 'bg-blue-500/90'
      } ${className}`}
      data-testid="evidence-sync-status"
    >
      {isFailed ? (
        <>
          <AlertTriangle className="w-3 h-3 text-white" />
          <span className="text-xs text-white font-medium">
            {lang === 'es' ? 'Error' : 'Failed'}
          </span>
          {onRetry && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                onRetry()
              }}
              className="ml-1 text-xs text-white underline hover:no-underline"
            >
              {lang === 'es' ? 'Reintentar' : 'Retry'}
            </button>
          )}
        </>
      ) : isLocal ? (
        <>
          <CloudOff className="w-3 h-3 text-white" />
          <span className="text-xs text-white font-medium">
            {lang === 'es' ? 'Local' : 'Local'}
          </span>
        </>
      ) : (
        <>
          <RefreshCw className="w-3 h-3 text-white animate-spin" />
          <span className="text-xs text-white font-medium">
            {lang === 'es' ? 'Subiendo...' : 'Uploading...'}
          </span>
        </>
      )}
    </div>
  )
}

// Compact sync indicator for lists
export function SyncIndicator({ isSynced, className = '' }) {
  if (isSynced) {
    return (
      <Check 
        className={`w-3 h-3 text-emerald-500 ${className}`} 
        data-testid="sync-indicator-synced"
      />
    )
  }
  
  return (
    <Clock 
      className={`w-3 h-3 text-amber-500 ${className}`}
      data-testid="sync-indicator-pending"
    />
  )
}

export default OfflineBanner
